<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px] tw-overflow-hidden tw-h-fit dark:tw-bg-gray-900 tw-bg-white tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                {{ $t("common.edit-sponsor") }}
            </div>

            <div class="tw-p-5 tw-bg-white">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-0">
                    <div class="tw-col-span-12 md:tw-col-span-12">
                        <FormField 
                            left-icon="solar:user-circle-bold-duotone"
                            v-model="form.name"
                            :label="$t('fields.name')"
                            type="text"
                            :error="errors.name && $t('errors.' + errors.name, {
                                field: $t('fields.name'),
                            })"
                        />
                    </div>

                    <div class="tw-col-span-12 md:tw-col-span-12">
                        <FormFileUpload
                            v-model="form.image"
                            :label="$t('fields.image')"
                            :placeholder="$t('fields.image-placeholder')"
                            :help="$t('fields.image-help')"
                            left-icon="solar:image-circle-bold-duotone"
                            accept="image/*"
                            :error="errors.image && $t('errors.' + errors.image, {
                                field: $t('fields.image'),
                            })"
                        />
                    </div>

                    <div class="tw-col-span-12 md:tw-col-span-12">
                        <FormField 
                            left-icon="solar:link-circle-bold-duotone"
                            v-model="form.link"
                            :label="$t('fields.link')"
                            type="url"
                            :error="errors.link && $t('errors.' + errors.link, {
                                field: $t('fields.link'),
                            })"
                        />
                    </div>

                    <div class="tw-col-span-12 md:tw-col-span-12">
                        <FormField 
                            v-model="form.description"
                            :label="$t('fields.description')"
                            type="textarea"
                            :error="errors.description && $t('errors.' + errors.description, {
                                field: $t('fields.description'),
                            })"
                        />
                    </div>

                    <div class="tw-col-span-12 md:tw-col-span-12">
                        <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                            {{ $t('fields.is_active') }}
                        </label>
                        <div class="tw-flex tw-gap-2">
                            <a-button
                                :intent="form.is_active ? 'primary' : 'simple'"
                                class="tw-flex-1"
                                @click="form.is_active = true"
                            >
                                <template #prefix>
                                    <i class="ri-eye-line tw-mr-1"></i>
                                </template>
                                {{ $t('common.active') }}
                            </a-button>
                            <a-button
                                :intent="!form.is_active ? 'primary' : 'simple'"
                                class="tw-flex-1"
                                @click="form.is_active = false"
                            >
                                <template #prefix>
                                    <i class="ri-eye-off-line tw-mr-1"></i>
                                </template>
                                {{ $t('common.inactive') }}
                            </a-button>
                        </div>
                        <div v-if="errors.is_active" class="tw-mt-1 tw-text-sm tw-text-red-600">
                            {{ $t('errors.' + errors.is_active, {
                                field: $t('fields.is_active'),
                            }) }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="tw-p-4 tw-text-lg tw-border-t tw-justify-end tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                <a-button 
                    :disabled="loading" 
                    @click="onCancel" 
                    class="tw-min-w-[120px]"
                    intent="simple"
                >
                    {{ $t("buttons.cancel") }}
                </a-button>
                <a-button 
                    :disabled="loading" 
                    :loading="loading" 
                    @click="handleSubmit"
                    class="tw-min-w-[120px]"
                >
                    {{ $t("buttons.save") }}
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import FormFileUpload from '@/components/form/FormFileUpload';
import useAlert from '@/composables/useAlert';
import sponsorsApi from '@/api/sponsors';

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
    sponsor: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['close', 'updated']);

const { t } = useI18n();
const loading = ref(false);
const errors = ref({});

const form = ref({
    name: '',
    image: null,
    link: '',
    description: '',
    is_active: true
});

onMounted(() => {
    // Initialize form with sponsor data
    form.value = {
        name: props.sponsor.name,
        image: props.sponsor.image || null, // Pass the image URL directly
        link: props.sponsor.link || '',
        description: props.sponsor.description || '',
        is_active: props.sponsor.is_active
    };
});

const onCancel = () => {
    errors.value = {};
    emit('close');
};

const handleSubmit = async () => {
    loading.value = true;
    errors.value = {};

    try {
        // Always use FormData for consistency with the API
        const formData = new FormData();
        formData.append('name', form.value.name);
        if (form.value.image instanceof File) { // Only append if it's a new file
            formData.append('image', form.value.image);
        }
        if (form.value.link) {
            formData.append('link', form.value.link);
        }
        if (form.value.description) {
            formData.append('description', form.value.description);
        }
        formData.append('is_active', form.value.is_active ? '1' : '0');

        console.log('Submitting data:', Object.fromEntries(formData));
        const response = await sponsorsApi.admin.update(props.sponsor.id, formData);
        console.log('Response:', response);
        
        if (response?.data) {
            emit('updated');
            onCancel();
        }
    } catch (error) {
        console.error('Error details:', error);
        if (error.response?.status === 422) {
            for (let e in error.response.data.errors) {
                errors.value[e] = error.response.data.errors[e][0];
            }
        } else {
            useAlert(t('errors.something-went-wrong'), 'danger');
        }
    } finally {
        loading.value = false;
    }
};
</script>
